import React, { useEffect, useState } from "react";
import { NavLink, useNavigate, Link, Outlet, useLocation } from "react-router-dom";
import {
  LayoutDashboard, FileSpreadsheet, Users, Scale, Clock, Workflow, Receipt, ListChecks, FileSignature,
  Building2, Inbox, Plus, LogOut, ChevronDown, ChevronRight, Settings as SettingsIcon, Search, Newspaper, Sparkles,
  MessageSquare, FileText, UploadCloud, Globe, LineChart, TrendingUp, GitMerge,
} from "lucide-react";
import { fetchAdminMe, adminLogout, getAdmin } from "../lib/auth";
import { BrandMark } from "./Brand";
import GeneratePayoffModal from "./payoffs/GeneratePayoffModal";
import AlertsBell from "./AlertsBell";

// Top-level nav items rendered as flat links.
const NAV = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true, testid: "nav-dashboard" },
  { to: "/admin/loans", label: "Loans", icon: FileSpreadsheet, testid: "nav-loans" },
  { to: "/admin/clients", label: "Clients", icon: Users, testid: "nav-clients" },
  { to: "/admin/attorneys", label: "Attorneys", icon: Scale, testid: "nav-attorneys" },
  { to: "/admin/firms", label: "Firms", icon: Building2, testid: "nav-firms" },
  { to: "/admin/requests", label: "Requests", icon: Inbox, testid: "nav-requests" },
  { to: "/admin/online-applications", label: "Online Applications", icon: Globe, testid: "nav-online-applications" },
  { to: "/admin/contracts", label: "Agreements", icon: FileSignature, testid: "nav-contracts" },
  { to: "/admin/templates", label: "Templates", icon: FileText, testid: "nav-templates" },
  { to: "/admin/payoffs", label: "Payoffs", icon: Receipt, testid: "nav-payoffs" },
  { to: "/admin/data-quality/client-groups", label: "Client Grouping", icon: GitMerge, testid: "nav-client-grouping" },
  { to: "/admin/tasks", label: "Tasks", icon: ListChecks, testid: "nav-tasks" },
  { to: "/admin/aging", label: "Aging", icon: Clock, testid: "nav-aging" },
  { to: "/admin/pipeline", label: "Pipeline", icon: Workflow, testid: "nav-pipeline" },
  { to: "/admin/communications", label: "Communications", icon: MessageSquare, testid: "nav-communications" },
  { to: "/admin/blog", label: "Blog CMS", icon: Newspaper, testid: "nav-blog" },
  { to: "/admin/blog/topics", label: "Content Roadmap", icon: Sparkles, testid: "nav-blog-topics" },
  { to: "/admin/content-sync", label: "Publish to Prod", icon: UploadCloud, testid: "nav-content-sync" },
  { to: "/admin/template-deploy", label: "Deploy Templates", icon: UploadCloud, testid: "nav-template-deploy" },
];

// Single Finance & Analytics group rendered as one collapsible parent.
// URLs are unchanged from the earlier flat nav so bookmarks/deep links continue to work.
const FINANCE_GROUP = {
  label: "Finance & Analytics",
  icon: TrendingUp,
  testid: "nav-finance-group",
  children: [
    // Portfolio
    { to: "/admin/finance/portfolio",             label: "Portfolio Overview",   testid: "nav-finance-portfolio" },
    { to: "/admin/finance/unit-economics",        label: "Unit Economics",       testid: "nav-finance-unit-econ" },
    { to: "/admin/finance/vintage",               label: "Vintage / Cohorts",    testid: "nav-finance-vintage" },
    { to: "/admin/finance/relationships",         label: "Attorney / Firm",      testid: "nav-finance-relationships" },
    // Capital & Returns
    { to: "/admin/finance/capital",               label: "Capital & Recycling",  testid: "nav-finance-capital" },
    { to: "/admin/finance/capital-structures",    label: "Capital Structures",   testid: "nav-finance-capital-structures" },
    { to: "/admin/finance/contribution",          label: "Portfolio Contribution", testid: "nav-finance-contribution" },
    // Planning
    { to: "/admin/finance/pro-forma",             label: "Pro Forma",            testid: "nav-finance-pro-forma" },
    { to: "/admin/finance/sensitivity",           label: "Sensitivity & Compare", testid: "nav-finance-sensitivity" },
    { to: "/admin/finance/curves",                label: "Repayment / Loss Curves", testid: "nav-finance-curves" },
    // Reporting & Controls
    { to: "/admin/finance/assumptions",           label: "Assumptions & Reports", testid: "nav-finance-assumptions" },
    { to: "/admin/finance/settlement-diagnostic", label: "Settlement Diagnostic", testid: "nav-settlement-diagnostic" },
  ],
};

export default function AdminLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const [admin, setAdmin] = useState(getAdmin());
  const [bootstrapping, setBootstrapping] = useState(true);
  const [menuOpen, setMenuOpen] = useState(false);
  const [showPayoff, setShowPayoff] = useState(false);

  // Finance & Analytics group is auto-expanded when the user is on any Finance route.
  // Otherwise remembers whatever the user last chose (localStorage).
  const isOnFinance = location.pathname.startsWith("/admin/finance");
  const [financeOpen, setFinanceOpen] = useState(() => {
    try {
      const stored = localStorage.getItem("admin_nav_finance_open");
      if (stored !== null) return stored === "1";
    } catch {}
    return isOnFinance;
  });
  useEffect(() => {
    if (isOnFinance) setFinanceOpen(true);
  }, [isOnFinance]);
  const toggleFinance = () => {
    const next = !financeOpen;
    setFinanceOpen(next);
    try { localStorage.setItem("admin_nav_finance_open", next ? "1" : "0"); } catch {}
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const me = await fetchAdminMe();
        if (!cancelled) { setAdmin(me); setBootstrapping(false); }
      } catch {
        try { localStorage.removeItem("instabridge_admin"); } catch {}
        if (!cancelled) navigate("/login", { replace: true });
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line
  }, [location.pathname]);

  const logout = async () => { await adminLogout(); navigate("/login"); };

  if (bootstrapping) return (
    <div className="min-h-screen flex items-center justify-center text-[#6B7280]" data-testid="admin-bootstrap">
      Loading workspace…
    </div>
  );

  const user = admin?.user || admin;
  const role = user?.role || "admin";
  const initials = (user?.name || user?.email || "A").trim().split(/\s+/).slice(0, 2).map((s) => s[0]?.toUpperCase()).join("") || "A";

  return (
    <div className="min-h-screen bg-[#F9FAFB] flex" data-testid="admin-layout">
      {/* Sidebar */}
      <aside className="hidden lg:flex w-64 flex-col bg-[#0A2540] text-white border-r border-[#0A2540]/60 sticky top-0 h-screen">
        <div className="h-14 px-5 flex items-center border-b border-white/5">
          <BrandMark inverted />
        </div>
        <nav className="flex-1 overflow-y-auto py-3 px-2 space-y-0.5" data-testid="admin-sidebar-nav">
          {NAV.map((item) => {
            const link = (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                data-testid={item.testid}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 h-9 rounded-md text-sm transition-colors ${
                    isActive ? "bg-white/10 text-white font-medium" : "text-white/70 hover:text-white hover:bg-white/5"
                  }`
                }
              >
                <item.icon className="w-4 h-4 flex-shrink-0" />
                <span>{item.label}</span>
              </NavLink>
            );
            // Insert the Finance & Analytics collapsible group after "Aging"
            if (item.testid === "nav-aging") {
              return (
                <React.Fragment key={item.to}>
                  {link}
                  <button
                    type="button"
                    onClick={toggleFinance}
                    data-testid={FINANCE_GROUP.testid}
                    aria-expanded={financeOpen}
                    className={`w-full flex items-center gap-3 px-3 h-9 rounded-md text-sm transition-colors ${
                      isOnFinance ? "bg-white/10 text-white font-medium" : "text-white/70 hover:text-white hover:bg-white/5"
                    }`}
                  >
                    <FINANCE_GROUP.icon className="w-4 h-4 flex-shrink-0" />
                    <span className="flex-1 text-left">{FINANCE_GROUP.label}</span>
                    {financeOpen
                      ? <ChevronDown className="w-3.5 h-3.5" />
                      : <ChevronRight className="w-3.5 h-3.5" />}
                  </button>
                  {financeOpen && (
                    <div className="ml-2 pl-3 border-l border-white/10 space-y-0.5" data-testid="finance-group-children">
                      {FINANCE_GROUP.children.map((c) => (
                        <NavLink
                          key={c.to}
                          to={c.to}
                          data-testid={c.testid}
                          className={({ isActive }) =>
                            `flex items-center gap-2 px-3 h-8 rounded-md text-[13px] transition-colors ${
                              isActive ? "bg-white/10 text-white font-medium" : "text-white/60 hover:text-white hover:bg-white/5"
                            }`
                          }
                        >
                          <span>{c.label}</span>
                        </NavLink>
                      ))}
                    </div>
                  )}
                </React.Fragment>
              );
            }
            return link;
          })}
          <div className="pt-3 mt-3 border-t border-white/5">
            <NavLink
              to="/admin/invitations"
              data-testid="nav-invitations"
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 h-9 rounded-md text-sm transition-colors ${
                  isActive ? "bg-white/10 text-white font-medium" : "text-white/70 hover:text-white hover:bg-white/5"
                }`
              }
            >
              <SettingsIcon className="w-4 h-4" />
              <span>Settings</span>
            </NavLink>
          </div>
        </nav>
        <div className="p-3 border-t border-white/5">
          <Link
            to="/admin/loans/new"
            data-testid="sidebar-add-funding"
            className="flex items-center justify-center gap-2 h-10 rounded-md bg-[#0066FF] hover:bg-[#0052CC] text-white text-sm font-medium transition-colors"
          >
            <Plus className="w-4 h-4" /> Add funding
          </Link>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 min-w-0 flex flex-col">
        {/* Topbar */}
        <header className="h-14 bg-white border-b border-[#E5E7EB] sticky top-0 z-20 flex items-center px-4 lg:px-6 gap-4">
          <div className="lg:hidden"><BrandMark /></div>
          <div className="flex-1 max-w-xl">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9CA3AF]" />
              <input
                type="search"
                placeholder="Search clients, attorneys, loan IDs…"
                onKeyDown={(e) => {
                  if (e.key === "Enter" && e.target.value.trim()) {
                    navigate(`/admin/loans?search=${encodeURIComponent(e.target.value.trim())}`);
                  }
                }}
                className="w-full h-9 pl-9 pr-3 rounded-md border border-[#E5E7EB] bg-[#F9FAFB] text-sm placeholder:text-[#9CA3AF] focus:outline-none focus:ring-2 focus:ring-[#0066FF]/20 focus:border-[#0066FF]"
                data-testid="topbar-search"
              />
            </div>
          </div>
          <button
            onClick={() => setShowPayoff(true)}
            data-testid="topbar-generate-payoff"
            className="hidden sm:inline-flex items-center gap-1.5 h-9 px-3 rounded-md bg-[#0066FF] hover:bg-[#0052CC] text-white text-xs font-medium transition-colors"
          >
            <Receipt className="w-3.5 h-3.5" /> Generate payoff
          </button>
          <AlertsBell />
          <div className="relative">
            <button
              onClick={() => setMenuOpen((v) => !v)}
              data-testid="user-menu-btn"
              className="flex items-center gap-2 h-9 px-2 rounded-md hover:bg-[#F9FAFB]"
            >
              <div className="w-7 h-7 rounded-full bg-[#0A2540] text-white text-xs font-medium flex items-center justify-center tabular-nums">{initials}</div>
              <div className="hidden sm:block text-left">
                <div className="text-xs font-medium text-[#0A2540] leading-tight">{user?.name || user?.email}</div>
                <div className="text-[10px] uppercase tracking-wider text-[#6B7280]">{role}</div>
              </div>
              <ChevronDown className="w-3.5 h-3.5 text-[#6B7280]" />
            </button>
            {menuOpen && (
              <div className="absolute right-0 mt-2 w-56 bg-white border border-[#E5E7EB] rounded-lg shadow-lg py-1.5 z-40" data-testid="user-menu">
                <div className="px-3 py-2 border-b border-[#F3F4F6]">
                  <div className="text-sm font-medium text-[#0A2540]">{user?.name || "Admin"}</div>
                  <div className="text-xs text-[#6B7280]">{user?.email}</div>
                </div>
                <Link to="/" className="flex items-center gap-2 px-3 py-2 text-sm text-[#374151] hover:bg-[#F9FAFB]">View public site</Link>
                <button onClick={logout} data-testid="logout-btn" className="w-full text-left flex items-center gap-2 px-3 py-2 text-sm text-[#B91C1C] hover:bg-[#FEF2F2]">
                  <LogOut className="w-3.5 h-3.5" /> Sign out
                </button>
              </div>
            )}
          </div>
        </header>

        <main className="flex-1 min-w-0">
          <Outlet />
        </main>
      </div>

      <GeneratePayoffModal open={showPayoff} onClose={() => setShowPayoff(false)} />
    </div>
  );
}
